import { BaseSyntheticEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { TRIGGERS_BASE_QUERY_KEY } from 'components/domain/automation/triggers/triggersQuery';
import useCreateTriggerMutation from 'components/domain/automation/triggers/useCreateTriggerMutation';
import useFetchTriggerOptions from 'components/domain/automation/triggers/useFetchTriggerOptions';
import useUpdateTriggerMutation from 'components/domain/automation/triggers/useUpdateTriggerMutation';
import { FormContextParts } from 'components/forms/form/Form.types';
import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import { Trigger, TriggersCreatePayload } from 'models/automation/Triggers';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { TriggerAddEditModalFormValues } from './TriggerAddEditModal.schema';
import {
  getActionsRelatedFieldsForPayload,
  getConditionFieldForPayload,
  getTriggerEventTypeFieldForPayload,
} from './TriggerAddEditModalFormSubmit.utils';

const useTriggerAddEditModalFormSubmit = ({
  originalTrigger,
  closeModal,
}: {
  originalTrigger?: Trigger | Omit<Trigger, 'uid'>;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useNotify();
  const { data: triggerOptions } = useFetchTriggerOptions();

  const onError = (error: BaseErrorResponse) => {
    notifyError(
      getErrorMessage({
        apiErrorCode: error.response?.data?.apiErrorCode,
        apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        baseKey: 'pageAutomations.triggers.apiErrorCodes.',
        t,
      }),
    );
  };

  const { mutateAsync: createTrigger } = useCreateTriggerMutation({ onError });
  const { mutateAsync: updateTrigger } = useUpdateTriggerMutation({ onError });

  const onSubmit = async (
    formValues: TriggerAddEditModalFormValues,
    _event: BaseSyntheticEvent | undefined,
    { reset }: FormContextParts<TriggerAddEditModalFormValues>,
  ) => {
    const isNew = !originalTrigger || !('uid' in originalTrigger);
    const { eventType, name, triggerType } = formValues;

    // triggerType should always be present given the form validation.
    if (!triggerType || !eventType) {
      return;
    }

    const condition = getConditionFieldForPayload({ formValues });

    const commonPayload: TriggersCreatePayload = {
      condition,
      name,
      eventType: getTriggerEventTypeFieldForPayload({
        condition,
        eventType,
      }),
      triggerType,
      ...getActionsRelatedFieldsForPayload({ formValues, triggerOptions }),
    };

    if (isNew) {
      await createTrigger(commonPayload);
    } else {
      await updateTrigger({
        ...commonPayload,
        uid: originalTrigger.uid,
      });
    }

    flushSync(() => reset(formValues, { keepDirty: false }));
    closeModal();

    if (isNew) {
      notifySuccess(t('pageAutomations.triggers.modal.successCreated'));
    } else {
      notifySuccess(t('pageAutomations.triggers.modal.successSaved'));
    }

    queryClient.invalidateQueries([TRIGGERS_BASE_QUERY_KEY]);
  };

  return { onSubmit };
};

export default useTriggerAddEditModalFormSubmit;
